// Minimal Standard MIDI File parser → { notes: [{pitch,start,duration,velocity}] (beats), bpm }
export function parseMidi(buffer) {
  const d = new DataView(buffer);
  let p = 0;
  const u32 = () => { const v = d.getUint32(p); p += 4; return v; };
  const u16 = () => { const v = d.getUint16(p); p += 2; return v; };
  const str = (n) => { let s = ""; for (let i = 0; i < n; i++) s += String.fromCharCode(d.getUint8(p++)); return s; };
  if (str(4) !== "MThd") throw new Error("Not a MIDI file");
  const hlen = u32();
  const format = u16(); const ntracks = u16(); const division = u16();
  p += hlen - 6;
  if (division & 0x8000) throw new Error("SMPTE time division not supported");
  const tpq = division || 480;
  let bpm = 120;
  const notes = [];
  for (let t = 0; t < ntracks && p < d.byteLength; t++) {
    if (str(4) !== "MTrk") break;
    const len = u32(); const end = p + len;
    let tick = 0; let running = 0;
    const open = {};
    const vlq = () => { let v = 0, b; do { b = d.getUint8(p++); v = (v << 7) | (b & 0x7f); } while (b & 0x80); return v; };
    while (p < end) {
      tick += vlq();
      let status = d.getUint8(p);
      if (status & 0x80) { p++; running = status; } else status = running;
      const type = status & 0xf0;
      if (status === 0xff) {
        const meta = d.getUint8(p++); const l = vlq();
        if (meta === 0x51 && l === 3) { const us = (d.getUint8(p) << 16) | (d.getUint8(p + 1) << 8) | d.getUint8(p + 2); if (us > 0 && tick === 0) bpm = Math.round(60000000 / us); }
        p += l;
      } else if (status === 0xf0 || status === 0xf7) { p += vlq(); }
      else if (type === 0x90 || type === 0x80) {
        const pitch = d.getUint8(p++); const vel = d.getUint8(p++);
        const ch = status & 0x0f; const key = `${ch}-${pitch}`;
        if (type === 0x90 && vel > 0) open[key] = { tick, vel };
        else if (open[key]) {
          const o = open[key]; delete open[key];
          notes.push({ pitch, start: Math.round((o.tick / tpq) * 1000) / 1000, duration: Math.max(0.05, Math.round(((tick - o.tick) / tpq) * 1000) / 1000), velocity: o.vel });
        }
      } else if (type === 0xa0 || type === 0xb0 || type === 0xe0) p += 2;
      else if (type === 0xc0 || type === 0xd0) p += 1;
      else p++;
    }
    p = end;
  }
  notes.sort((a, b) => a.start - b.start);
  const t0 = notes.length ? notes[0].start : 0;
  return { notes: notes.map((n) => ({ ...n, start: Math.round((n.start - t0) * 1000) / 1000 })).filter((n) => n.pitch >= 12 && n.pitch <= 120).slice(0, 2000), bpm, format, tracks: ntracks };
}

// notes (beats) + bpm → Standard MIDI File (format 0) bytes
export function buildMidi(notes, bpm = 120, name = "MusicFlow pattern") {
  const tpq = 480;
  const vlq = (n) => { const b = [n & 0x7f]; n >>= 7; while (n > 0) { b.unshift((n & 0x7f) | 0x80); n >>= 7; } return b; };
  const ev = [];
  notes.forEach((n) => {
    const s = Math.round(n.start * tpq); const e = Math.max(s + 1, Math.round((n.start + n.duration) * tpq));
    ev.push({ t: s, b: [0x90, n.pitch & 0x7f, Math.max(1, Math.min(127, n.velocity || 100))], o: 1 });
    ev.push({ t: e, b: [0x80, n.pitch & 0x7f, 0], o: 0 });
  });
  ev.sort((a, b) => a.t - b.t || a.o - b.o);
  const us = Math.round(60000000 / bpm);
  const nm = Array.from(new TextEncoder().encode(name.slice(0, 60)));
  let trk = [...vlq(0), 0xff, 0x03, ...vlq(nm.length), ...nm, ...vlq(0), 0xff, 0x51, 0x03, (us >> 16) & 0xff, (us >> 8) & 0xff, us & 0xff, ...vlq(0), 0xff, 0x58, 0x04, 4, 2, 24, 8];
  let last = 0;
  ev.forEach((e) => { trk.push(...vlq(e.t - last), ...e.b); last = e.t; });
  trk.push(...vlq(0), 0xff, 0x2f, 0x00);
  const u32 = (v) => [(v >>> 24) & 0xff, (v >>> 16) & 0xff, (v >>> 8) & 0xff, v & 0xff];
  const bytes = [0x4d, 0x54, 0x68, 0x64, ...u32(6), 0, 0, 0, 1, (tpq >> 8) & 0xff, tpq & 0xff, 0x4d, 0x54, 0x72, 0x6b, ...u32(trk.length), ...trk];
  return new Uint8Array(bytes);
}

export function downloadMidi(notes, bpm, name) {
  const blob = new Blob([buildMidi(notes, bpm, name)], { type: "audio/midi" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${(name || "pattern").replace(/[^\w\-]+/g, "-")}.mid`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
